import { useState, useEffect } from 'react';
import {
  Box,
  Stack,
  Alert,
  Typography,
  TextField,
  Button,
  CircularProgress,
  Chip,
  Link,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { FormProvider } from 'react-hook-form';
import { useSitePanelState } from '../../../hooks/useSitePanelState';
import { usePlotDetails } from '../../../hooks/usePlotDetails';
import { useAddLabourItemForm } from '../../../hooks/useAddLabourItemForm';
import { LabourItemAutocomplete } from '../../organisms/LabourItemAutocomplete';
import { PriceInputField } from '../../molecules/PriceInputField';
import { ConfirmAddLabourItemDialog } from '../../molecules/ConfirmAddLabourItemDialog';

interface AddLabourItemPanelProps {
  plotId: number;
}

interface RecentlyAddedItem {
  name: string;
  price: number;
}

/**
 * Panel for adding labour items to a plot
 * Follows panel pattern like LabourItemDetailsPanel
 */
export function AddLabourItemPanel({ plotId }: AddLabourItemPanelProps) {
  const { siteId, closePanel } = useSitePanelState();
  const siteIdNum = siteId ? parseInt(siteId, 10) : 0;
  const { data: plot } = usePlotDetails(siteIdNum || null, plotId);
  const [recentlyAdded, setRecentlyAdded] = useState<RecentlyAddedItem[]>([]);
  const [showConfirm, setShowConfirm] = useState(false);
  const [pendingData, setPendingData] = useState<any>(null);
  const [isCreatingNew, setIsCreatingNew] = useState(false);

  const {
    form,
    handleSubmit: handleFormSubmit,
    isSubmitting,
    successMessage,
    errorMessage,
  } = useAddLabourItemForm(siteIdNum, plotId, {
    onSuccess: () => {
      // Close panel after successful submission
      closePanel();
    },
  });

  const {
    handleSubmit,
    formState: { errors },
    register,
    watch,
  } = form;

  const selectedDefinition = watch('selectedDefinition');

  // Clear state when panel mounts (fresh open)
  useEffect(() => {
    setRecentlyAdded([]);
    setIsCreatingNew(false);
  }, []);

  // Handle form submission - show confirmation dialog
  const onSubmit = async (data: any) => {
    setPendingData(data);
    setShowConfirm(true);
  };

  // Handle confirmation - actually submit the form
  const handleConfirm = async () => {
    setShowConfirm(false);
    if (pendingData) {
      await handleFormSubmit(pendingData);

      // Add to recently added list if successful
      if (successMessage) {
        const newItem = {
          name: isCreatingNew
            ? pendingData.name
            : pendingData.selectedDefinition?.name,
          price: pendingData.price,
        };
        setRecentlyAdded(prev => [newItem, ...prev].slice(0, 3));
      }
    }
  };

  // Handle cancel confirmation
  const handleCancelConfirm = () => {
    setShowConfirm(false);
    setPendingData(null);
  };

  // No plot data (initial loading or truly no data)
  if (!plot) {
    return (
      <Box sx={{ p: 3 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box
      sx={{ p: 3, height: '100%', display: 'flex', flexDirection: 'column' }}
    >
      {/* Success/Error messages */}
      {successMessage && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {successMessage}
        </Alert>
      )}

      {errorMessage && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {errorMessage}
        </Alert>
      )}

      {/* Info banner */}
      {!isCreatingNew && (
        <Alert severity="info" sx={{ mb: 3 }}>
          Select an existing labour item type to add to this plot
        </Alert>
      )}

      {/* Form */}
      <FormProvider {...form}>
        <Box
          component="form"
          onSubmit={handleSubmit(onSubmit)}
          sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}
        >
          <Stack spacing={3} sx={{ flex: 1 }}>
            {/* Mode: Select existing */}
            {!isCreatingNew && (
              <>
                <Box>
                  <LabourItemAutocomplete
                    value={selectedDefinition ?? null}
                    onChange={value => {
                      form.setValue('selectedDefinition', value as any);
                    }}
                    error={errors.selectedDefinition?.message?.toString()}
                  />
                </Box>
                <Box>
                  <Link
                    component="button"
                    type="button"
                    variant="body2"
                    onClick={() => {
                      setIsCreatingNew(true);
                      form.setValue('selectedDefinition', null);
                    }}
                    sx={{ textDecoration: 'none', cursor: 'pointer' }}
                  >
                    Or create a new labour item type
                  </Link>
                </Box>
              </>
            )}

            {/* Mode: Create new */}
            {isCreatingNew && (
              <>
                <Alert severity="info">
                  You are creating a new labour item type. This will be
                  available for future use across all plots.
                </Alert>
                <TextField
                  label="Labour Item Name *"
                  {...register('name')}
                  error={!!errors.name}
                  helperText={errors.name?.message?.toString()}
                  fullWidth
                />
                <Box>
                  <Link
                    component="button"
                    type="button"
                    variant="body2"
                    onClick={() => {
                      setIsCreatingNew(false);
                      form.setValue('name', undefined);
                    }}
                    sx={{ textDecoration: 'none', cursor: 'pointer' }}
                  >
                    Cancel
                  </Link>
                </Box>
              </>
            )}

            {/* Price field */}
            <PriceInputField />

            {/* Optional description */}
            <TextField
              label="Description (Optional)"
              {...register('description')}
              error={!!errors.description}
              helperText={errors.description?.message?.toString()}
              multiline
              rows={3}
              fullWidth
            />
          </Stack>

          {/* Recently added items */}
          {recentlyAdded.length > 0 && (
            <Box sx={{ mt: 3 }}>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ mb: 1, display: 'block' }}
              >
                Recently Added
              </Typography>
              <Stack spacing={1}>
                {recentlyAdded.map((item, index) => (
                  <Box
                    key={index}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      p: 1,
                      backgroundColor: 'success.50',
                      borderRadius: 1,
                    }}
                  >
                    <CheckCircleIcon color="success" fontSize="small" />
                    <Typography variant="body2" sx={{ flex: 1 }}>
                      {item.name}
                    </Typography>
                    <Chip label={`£${item.price.toFixed(2)}`} size="small" />
                  </Box>
                ))}
              </Stack>
            </Box>
          )}

          {/* Plot Information */}
          <Box
            sx={{ mt: 2, p: 2, backgroundColor: 'grey.50', borderRadius: 1 }}
          >
            <Typography variant="body2" color="text.secondary">
              Adding to:{' '}
              <strong>
                {plot.name} ({plot.plotNumber})
              </strong>
            </Typography>
          </Box>

          {/* Actions */}
          <Box display="flex" gap={2} mt={3}>
            <Button
              type="submit"
              variant="contained"
              disabled={isSubmitting}
              fullWidth
            >
              {isSubmitting ? (
                <CircularProgress size={24} />
              ) : (
                'Add Labour Item'
              )}
            </Button>
            <Button
              variant="outlined"
              onClick={() => closePanel()}
              disabled={isSubmitting}
            >
              Close
            </Button>
          </Box>
        </Box>
      </FormProvider>

      {/* Confirmation Dialog */}
      <ConfirmAddLabourItemDialog
        open={showConfirm}
        onClose={handleCancelConfirm}
        onConfirm={handleConfirm}
        labourItemName={
          isCreatingNew
            ? pendingData?.name
            : pendingData?.selectedDefinition?.name
        }
        plotName={plot?.name}
        plotNumber={plot?.plotNumber}
      />
    </Box>
  );
}
